"use client";

import { useEffect, useRef } from 'react';

type TurnstileRenderOptions = {
    sitekey: string;
    theme?: "light" | "dark" | "auto";
};

type TurnstileAPI = {
    render: (element: HTMLElement, options: TurnstileRenderOptions) => string;
    remove: (widgetId: string) => void;
};

type TurnstileWindow = Window & { turnstile?: TurnstileAPI };

interface Props {
    siteKey?: string | { value?: string } | null;
    theme?: "light" | "dark" | "auto";
}

// Client-only helper to render Cloudflare Turnstile widget and ensure the script is loaded once.
export default function CloudflareTurnstile({ siteKey, theme = "dark" }: Props) {
    const normalizedKey = (() => {
        if (typeof siteKey === "string") return siteKey.trim();
        if (siteKey && typeof siteKey === "object" && "value" in siteKey && typeof siteKey.value === "string") {
            return siteKey.value.trim();
        }
        return "";
    })();
    const widgetRef = useRef<HTMLDivElement | null>(null);
    const widgetIdRef = useRef<string | null>(null);
    const scriptLoadedRef = useRef(false);
    const scriptSrc = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

    useEffect(() => {
        if (typeof window === "undefined" || !normalizedKey) return;

        console.debug("CloudflareTurnstile: initializing widget with site key", normalizedKey);

        const ensureScript = () => {
            if (scriptLoadedRef.current) return;
            if (document.querySelector(`script[src="${scriptSrc}"]`)) {
                scriptLoadedRef.current = true;
                return;
            }

            const script = document.createElement("script");
            script.src = scriptSrc;
            script.defer = true;
            script.onload = () => {
                scriptLoadedRef.current = true;
            };
            document.body.appendChild(script);
        };

        ensureScript();
    }, [normalizedKey]);

    useEffect(() => {
        const render = () => {
            if (!widgetRef.current) return;
            const turnstile = (window as TurnstileWindow).turnstile;
            if (!turnstile) return;
            if (widgetIdRef.current) {
                turnstile.remove(widgetIdRef.current);
                widgetIdRef.current = null;
            }
            widgetIdRef.current = turnstile.render(widgetRef.current, {
                sitekey: normalizedKey,
                theme,
            });
        };

        // If the script is already available, render immediately; otherwise poll briefly until it loads.
        let interval: number | undefined;
        if (!normalizedKey) {
            if (process.env.NODE_ENV === "development") {
                console.warn("CloudflareTurnstile: missing site key; widget not rendered");
            }
            return;
        }

        if ((window as TurnstileWindow).turnstile) {
            render();
        } else {
            interval = window.setInterval(() => {
                if ((window as TurnstileWindow).turnstile) {
                    window.clearInterval(interval);
                    render();
                }
            }, 100);
        }

        return () => {
            if (interval) window.clearInterval(interval);
            const turnstile = (window as TurnstileWindow).turnstile;
            if (turnstile && widgetIdRef.current) {
                turnstile.remove(widgetIdRef.current);
                widgetIdRef.current = null;
            }
        };
    }, [normalizedKey, theme]);

    return <div ref={widgetRef} />;
}
